import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { reportsApiSlice } from '@/store/slices/api/reportsApiSlice.ts';
import { appointmentListStatus } from '@/utils/appointmentTypeLabel';
import { API_ENDPOINTS, API_SLICE, BASE_URL } from '@/utils/constants/apiEndpoints.ts';

import { Lead } from '@/pages/physician/TransmissionReportPage/utils/types';
import {
  CreateInsuranceBody,
  InsuranceListResponse,
  InsurancePolicy,
  UpdateInsuranceBody
} from '@/types/insurance';
import { Lead as LeadPayload } from '@/types/matchDevice';
import { XifinOrderingPhysicianInput, XifinOrderingPhysicianResponse } from '@/types/npi';
import {
  FindAllAuthorizedPractitionersResponse,
  FindAllInClinicPractitionersResponse,
  GetAllPatientsRequestParams,
  GetPatientDiagnosesParams,
  GetPatientSchedulesRequest,
  GetPatientSchedulesResponse,
  GetTotalPatientsMonitoredRequest,
  GetTotalPatientsMonitoredResponse,
  MiniPatient,
  Patient,
  PatientDiagnosesResponse,
  PatientsNextScheduleTransmission,
  PatientsResponse,
  PatientUpdateOverviewDetails,
  UpdatePatientDto
} from '@/types/patient';
import {
  GetPatientTxReportsRequest,
  GetPatientTxReportsResponse,
  ReportType
} from '@/types/transmissionReport';
import { User } from '@/types/user';

import { CheckedPatients, setCheckedPatients } from '../patients/patientsSlice';

import { reportsPatientApiSlice } from './transmissionReport/reportsPatientApiSlice';
import { transmissionReportApiSlice } from './transmissionReport/transmissionReportsApiSlice';

import type { AppDispatch, RootState } from '@/store';

function invalidateReportPatient(patientId: string, dispatch: AppDispatch): void {
  if (!patientId) return;
  dispatch(reportsPatientApiSlice.util.invalidateTags([{ type: 'ReportPatient', id: patientId }]));
}

export const patientsApiSlice = createApi({
  reducerPath: 'patientsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: [
    API_SLICE.Patient,
    API_SLICE.TransmissionReport,
    API_SLICE.Insurance,
    API_SLICE.LeadChambers,
    API_SLICE.XifinOrderingPhysician
  ],
  endpoints: (builder) => ({
    getAllPatients: builder.query<PatientsResponse, GetAllPatientsRequestParams>({
      query: (params) => ({
        url: API_ENDPOINTS.PATIENTS.LIST,
        method: 'POST',
        body: params
      }),
      async onQueryStarted(_query, { dispatch, queryFulfilled, getState }) {
        const { patients } = getState() as RootState;
        if (!patients.isAllPatientsChecked) return;

        // NOTE: this is for the select all workflow
        try {
          const { data } = await queryFulfilled;

          const allPatients = data.patients.map((patient) => {
            const transmissionData = (patient.reportData || [])
              .filter(
                (tx) =>
                  !tx.downloaded &&
                  (patient.clinic?.ehrIntegration
                    ? tx.transmissiontype !== ReportType.Remote
                    : true)
              )
              .map((tx) => ({ transmissionId: tx.id }));

            return {
              patientId: patient.id,
              transmissionData
            } as CheckedPatients;
          });

          if (patients.isAllPatientsChecked) {
            dispatch(setCheckedPatients(allPatients));
          }
        } catch (error) {
          console.log('Error in Get All patients', error);
        }
      },
      providesTags: (_result, _error, _params) => [{ type: API_SLICE.Patient, id: 'LIST' }]
    }),
    getPatientsMonitoredMonthlyData: builder.query<
      GetTotalPatientsMonitoredResponse,
      GetTotalPatientsMonitoredRequest
    >({
      query: (params) => ({
        url: API_ENDPOINTS.PATIENTS.FILTERED_TOTAL_MONITORED,
        method: 'POST',
        body: params
      })
    }),
    getPatient: builder.query<Patient, string>({
      query: (patientId) => ({
        url: `${API_ENDPOINTS.PATIENTS.LIST}/${patientId}`,
        method: 'GET',
        cache: 'no-store'
      }),
      transformResponse: (response: { patient: Patient }) => response.patient,
      providesTags: (_result, _error, patientId) => [{ type: API_SLICE.Patient, id: patientId }]
    }),
    getXifinOrderingPhysician: builder.query<XifinOrderingPhysicianResponse, string>({
      query: (patientId) => ({
        url: `${API_ENDPOINTS.PATIENTS.LIST}/${patientId}/xifin-ordering-physician`,
        method: 'GET'
      }),
      providesTags: (_result, _error, patientId) => [
        { type: API_SLICE.XifinOrderingPhysician, id: patientId }
      ]
    }),
    updateXifinOrderingPhysician: builder.mutation<
      XifinOrderingPhysicianResponse,
      { patientId: string; body: XifinOrderingPhysicianInput }
    >({
      query: ({ patientId, body }) => ({
        url: `${API_ENDPOINTS.PATIENTS.LIST}/${patientId}/xifin-ordering-physician`,
        method: 'PUT',
        body
      }),
      invalidatesTags: (_result, error, { patientId }) =>
        error
          ? []
          : [
              { type: API_SLICE.XifinOrderingPhysician, id: patientId },
              { type: API_SLICE.Patient, id: patientId }
            ],
      async onQueryStarted({ patientId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          invalidateReportPatient(patientId, dispatch);
        } catch {
          // Mutation state surfaces the request failure to the component.
        }
      }
    }),
    clearXifinOrderingPhysician: builder.mutation<void, string>({
      query: (patientId) => ({
        url: `${API_ENDPOINTS.PATIENTS.LIST}/${patientId}/xifin-ordering-physician`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, error, patientId) =>
        error
          ? []
          : [
              { type: API_SLICE.XifinOrderingPhysician, id: patientId },
              { type: API_SLICE.Patient, id: patientId }
            ],
      async onQueryStarted(patientId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          invalidateReportPatient(patientId, dispatch);
        } catch {
          // Mutation state surfaces the request failure to the component.
        }
      }
    }),
    getMiniPatient: builder.query<MiniPatient, string>({
      query: (patientId) => ({
        url: `${API_ENDPOINTS.PATIENTS.LIST}/${patientId}/mini`,
        method: 'GET'
      }),
      transformResponse: (response: { patient: MiniPatient }) => response.patient,
      providesTags: (_result, _error, patientId) => [{ type: API_SLICE.Patient, id: patientId }]
    }),
    getAuthorizedPractitioners: builder.query<User[], { patientId: string }>({
      query: ({ patientId }) => ({
        url: API_ENDPOINTS.PATIENTS.AUTHORIZED_PRACTITIONERS(patientId),
        method: 'GET'
      }),
      transformResponse: (response: FindAllAuthorizedPractitionersResponse) =>
        response.practitioners
    }),
    getInClinicPractitioners: builder.query<User[], { patientId: string }>({
      query: ({ patientId }) => ({
        url: API_ENDPOINTS.PATIENTS.IN_CLINIC_PRACTITIONERS(patientId),
        method: 'GET'
      }),
      transformResponse: (response: FindAllInClinicPractitionersResponse) => response.practitioners
    }),
    getPatientsNextSchedules: builder.query<PatientsNextScheduleTransmission, string>({
      query: (patientId) => ({
        url: `${API_ENDPOINTS.SCHEDULE_TRANSMISSION.LIST}/${patientId}/find-upcoming-Scheduled-transmission`,
        method: 'GET'
      }),
      transformResponse: (response: PatientsNextScheduleTransmission) => response
    }),
    getPatientDiagnoses: builder.infiniteQuery<
      PatientDiagnosesResponse,
      GetPatientDiagnosesParams,
      number
    >({
      query: ({ queryArg, pageParam = 1 }) => ({
        url: API_ENDPOINTS.PATIENTS.DIAGNOSES(queryArg.patientId),
        method: 'GET',
        params: {
          ...queryArg,
          page: pageParam,
          limit: queryArg.limit ?? 50
        }
      }),
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          const nextPage = lastPageParam + 1;
          return nextPage <= lastPage.totalPages ? nextPage : undefined;
        }
      },
      providesTags: (_result, _error, queryArg) => [
        { type: API_SLICE.Patient, id: `${queryArg.patientId}-diagnoses` }
      ]
    }),
    addDiagnosisCode: builder.mutation<void, { diagnosisCode: string; patientId: string }>({
      query: ({ diagnosisCode, patientId }) => ({
        url: API_ENDPOINTS.PATIENTS.DIAGNOSES(patientId),
        method: 'POST',
        body: { diagnosisCode }
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: API_SLICE.Patient, id: `${patientId}-diagnoses` }
      ]
    }),
    deleteDiagnosisCode: builder.mutation<void, { diagnosisId: string; patientId: string }>({
      query: ({ diagnosisId, patientId }) => ({
        url: `${API_ENDPOINTS.PATIENTS.DIAGNOSES(patientId)}/${diagnosisId}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: API_SLICE.Patient, id: `${patientId}-diagnoses` }
      ]
    }),
    getPatientSchedules: builder.query<
      GetPatientSchedulesResponse,
      { patientId: string } & GetPatientSchedulesRequest
    >({
      query: ({ patientId, status, ...params }) => {
        const listStatus = appointmentListStatus(status);
        return {
          url: `/patients/v2/${patientId}/schedules`,
          method: 'POST',
          body: {
            ...params,
            ...(listStatus ? { status: listStatus } : {})
          }
        };
      },
      providesTags: (_result, _error, { patientId }) => [
        { type: API_SLICE.Patient, id: `${patientId}-schedules` }
      ]
    }),
    getPatientTransmissionReports: builder.infiniteQuery<
      GetPatientTxReportsResponse,
      { patientId: string } & GetPatientTxReportsRequest,
      number
    >({
      query: ({ queryArg, pageParam = 1 }) => {
        const { patientId, ...rest } = queryArg;
        return {
          url: API_ENDPOINTS.PATIENTS.TRANSMISSION_REPORTS(patientId),
          method: 'POST',
          body: {
            ...rest,
            page: pageParam,
            limit: queryArg.limit ?? 10
          }
        };
      },
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          const nextPage = lastPageParam + 1;
          return nextPage <= lastPage.totalPages ? nextPage : undefined;
        }
      },
      providesTags: (_result, _error, queryArg) => [
        { type: API_SLICE.Patient, id: `${queryArg.patientId}-reports-infinite` }
      ]
    }),
    getPatientTransmissionReportsPage: builder.query<
      GetPatientTxReportsResponse,
      { patientId: string; page: number } & GetPatientTxReportsRequest
    >({
      query: ({ patientId, page, ...rest }) => ({
        url: API_ENDPOINTS.PATIENTS.TRANSMISSION_REPORTS(patientId),
        method: 'POST',
        body: {
          ...rest,
          page,
          limit: rest.limit ?? 10
        }
      }),
      providesTags: (_result, _error, queryArg) => [
        { type: API_SLICE.Patient, id: `${queryArg.patientId}-reports` }
      ]
    }),
    updatePatient: builder.mutation<{ patient: Patient }, UpdatePatientDto>({
      query: ({ patient }) => {
        const body: any = {
          referringProvider: patient?.referringProvider?.NPI,
          referringPhysician: patient?.referringPhysician,
          oac: patient?.oac,
          practitionerId: patient?.practitioner?.id,
          diagnosisOption: patient?.diagnosisOption,
          monitorReason: patient?.monitorReason,
          birthDate: patient?.birthDate,
          sex: patient?.sex,
          mrn2: patient?.mrn2,
          inClinicPractitionerId: patient?.inClinicPractitioner?.id,
          patientPrintNote: patient?.patientPrintNote,
          ...(patient && Object.prototype.hasOwnProperty.call(patient, 'financialPayerOverride')
            ? { financialPayerOverride: patient.financialPayerOverride ?? null }
            : {}),
          ...(typeof patient?.isRhythmMonitored === 'boolean'
            ? { isRhythmMonitored: patient.isRhythmMonitored }
            : {})
        };

        // Add nested settings if present
        if (patient?.settings) {
          body.settings = patient.settings;
        }

        return {
          url: `${API_ENDPOINTS.PATIENTS.LIST}/${patient.id}`,
          method: 'PATCH',
          body
        };
      },
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
          const state = getState() as RootState;
          if (state.transmissionReports.activeReportId && args.patient.diagnosisOption) {
            dispatch(
              reportsApiSlice.util.invalidateTags([
                { type: API_SLICE.TransmissionReport, id: state.transmissionReports.activeReportId }
              ])
            );
          } else {
            console.log('Did not invalidate');
          }
        } catch (error) {
          // error catch
          console.log('Error in Update Patient', error);
        }
      },
      invalidatesTags: (_result, _error, { patient }) => [
        { type: API_SLICE.Patient, id: patient.id }
      ]
    }),

    updatePatientOverview: builder.mutation<
      Patient,
      { id: string; body: PatientUpdateOverviewDetails }
    >({
      query: ({ id, body }) => ({
        url: API_ENDPOINTS.PATIENTS.UPDATE_PATIENT(id),
        method: 'PATCH',
        body
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: API_SLICE.Patient, id }]
    }),
    getLeadChambers: builder.query<string[], void>({
      query: () => ({
        url: `${API_ENDPOINTS.PATIENTS.DEVICELEAD}/chambers`,
        method: 'GET'
      }),
      providesTags: [API_SLICE.LeadChambers]
    }),
    getDeviceLeads: builder.query<Lead[], string>({
      query: (deviceId) => ({
        url: `${API_ENDPOINTS.PATIENTS.DEVICELEAD}/${deviceId}`,
        method: 'GET'
      }),
      transformResponse: (response: Lead[]) => response
    }),
    updateDeviceLeads: builder.mutation<
      void,
      {
        patientId: string;
        deviceLeads: Partial<LeadPayload>[];
        deviceId: string;
        reportId?: number;
      }
    >({
      invalidatesTags: (_result, error) => (error ? [] : [API_SLICE.LeadChambers]),
      query: ({ deviceLeads, deviceId }) => ({
        url: `${API_ENDPOINTS.PATIENTS.DEVICELEAD}/${deviceId}`,
        method: 'POST',
        body: deviceLeads
      }),
      async onQueryStarted({ patientId, reportId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          invalidateReportPatient(patientId, dispatch);

          if (reportId) {
            dispatch(
              transmissionReportApiSlice.util.invalidateTags([
                { type: API_SLICE.TransmissionReport, id: reportId }
              ])
            );
          }
        } catch (error) {
          console.error('Error in Update Patient', error);
        }
      }
    }),
    getPatientInsurances: builder.query<InsurancePolicy[], string>({
      query: (patientId) => ({
        url: API_ENDPOINTS.PATIENTS.INSURANCES(patientId),
        method: 'GET'
      }),
      transformResponse: (response: InsuranceListResponse) => response.items ?? [],
      providesTags: (_result, _error, patientId) => [{ type: API_SLICE.Insurance, id: patientId }]
    }),
    createPatientInsurance: builder.mutation<
      InsurancePolicy,
      { patientId: string; body: CreateInsuranceBody }
    >({
      query: ({ patientId, body }) => ({
        url: API_ENDPOINTS.PATIENTS.INSURANCES(patientId),
        method: 'POST',
        body
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: API_SLICE.Insurance, id: patientId }
      ]
    }),
    updatePatientInsurance: builder.mutation<
      InsurancePolicy,
      { patientId: string; insuranceId: string; body: UpdateInsuranceBody }
    >({
      query: ({ patientId, insuranceId, body }) => ({
        url: `${API_ENDPOINTS.PATIENTS.INSURANCES(patientId)}/${insuranceId}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: API_SLICE.Insurance, id: patientId }
      ]
    }),
    reorderPatientInsurances: builder.mutation<
      InsuranceListResponse,
      { patientId: string; insuranceIds: string[] }
    >({
      query: ({ patientId, insuranceIds }) => ({
        url: `${API_ENDPOINTS.PATIENTS.INSURANCES(patientId)}/reorder`,
        method: 'POST',
        body: { insuranceIds }
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: API_SLICE.Insurance, id: patientId }
      ]
    })
  })
});

export const {
  useGetAllPatientsQuery,
  useGetPatientQuery,
  useGetXifinOrderingPhysicianQuery,
  useUpdateXifinOrderingPhysicianMutation,
  useClearXifinOrderingPhysicianMutation,
  useGetPatientsNextSchedulesQuery,
  useGetMiniPatientQuery,
  useGetPatientsMonitoredMonthlyDataQuery,
  useGetAuthorizedPractitionersQuery,
  useGetInClinicPractitionersQuery,
  useGetPatientDiagnosesInfiniteQuery,
  useUpdatePatientMutation,
  useAddDiagnosisCodeMutation,
  useDeleteDiagnosisCodeMutation,
  useGetPatientTransmissionReportsInfiniteQuery,
  useGetPatientTransmissionReportsPageQuery,
  useGetPatientSchedulesQuery,
  useLazyGetPatientSchedulesQuery,
  useUpdatePatientOverviewMutation,
  useGetLeadChambersQuery,
  useGetDeviceLeadsQuery,
  useUpdateDeviceLeadsMutation,
  useGetPatientInsurancesQuery,
  useCreatePatientInsuranceMutation,
  useUpdatePatientInsuranceMutation,
  useReorderPatientInsurancesMutation
} = patientsApiSlice;
